import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress, Avatar,
    Modal, ModalDialog, ModalClose, IconButton, Divider, Select, Option,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ReceiptLongRoundedIcon       from '@mui/icons-material/ReceiptLongRounded';
import PointOfSaleRoundedIcon       from '@mui/icons-material/PointOfSaleRounded';
import PaymentsRoundedIcon          from '@mui/icons-material/PaymentsRounded';
import CreditCardRoundedIcon        from '@mui/icons-material/CreditCardRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import LocationOnRoundedIcon        from '@mui/icons-material/LocationOnRounded';
import PersonRoundedIcon            from '@mui/icons-material/PersonRounded';
import AccessTimeRoundedIcon        from '@mui/icons-material/AccessTimeRounded';
import LogoutRoundedIcon            from '@mui/icons-material/LogoutRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import ArrowForwardRoundedIcon      from '@mui/icons-material/ArrowForwardRounded';
import BlockRoundedIcon             from '@mui/icons-material/BlockRounded';
import GavelRoundedIcon             from '@mui/icons-material/GavelRounded';
import BarChartRoundedIcon          from '@mui/icons-material/BarChartRounded';
import RefreshRoundedIcon           from '@mui/icons-material/RefreshRounded';
import AddRoundedIcon               from '@mui/icons-material/AddRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import BirthdayWidget from '../../components/BirthdayWidget';
import {
    APIWorkstationStats,
    APIWorkstationGetAdmins, APIWorkstationStartShift,
    APIWorkstationReassignPhotographer, APIGetLocations,
    APIWorkstationPhotographersStatus,
    APIWorkstationClaimPhotographer, APIWorkstationReleasePhotographer,
    APIGetPendingSaleNotifications,
    APIProcessSaleNotification, APICancelSaleNotification,
    APIGetProducts, APIGetDiscounts, APIGetProductCategories,
    parseApiError, APIDeleteOrder
} from '../../api';
import MemeLoader from '../../components/MemeLoader';
import WorkstationAddOrderModal from './WorkstationAddOrderModal';
import ProcessSaleModal from './ProcessSaleModal';
import PointPhotographersPanel from './PointPhotographersPanel';


// ─── Утилиты ──────────────────────────────────────────────────────────────────
const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};

// "Сегодня 20:45" / "Вчера 20:45" / "18.06 20:45"
const fmtDateTimeRelative = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

    const startOfDay = (date) => {
        const x = new Date(date);
        x.setHours(0, 0, 0, 0);
        return x;
    };
    const todayMid = startOfDay(new Date());
    const targetMid = startOfDay(d);
    const diffDays = Math.round((todayMid - targetMid) / 86_400_000);

    if (diffDays === 0) return `Сегодня ${time}`;
    if (diffDays === 1) return `Вчера ${time}`;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm} ${time}`;
};

const getInitials = (name = '') => {
    const parts = name.trim().split(' ');
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
};

const MONEY_META = {
    transfer: { label: 'Перевод',   color: '#818cf8', bg: 'rgba(129,140,248,0.10)', icon: <CreditCardRoundedIcon  sx={{ fontSize: 14 }} /> },
    cash:     { label: 'Наличные',  color: '#22c55e', bg: 'rgba(34,197,94,0.10)',   icon: <PaymentsRoundedIcon    sx={{ fontSize: 14 }} /> },
    terminal: { label: 'Терминал',  color: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  icon: <PointOfSaleRoundedIcon sx={{ fontSize: 14 }} /> },
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.06 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

// ════════════════════════════════════════════════════════════════════
//  Подкомпонент: выбор админа для начала смены
// ════════════════════════════════════════════════════════════════════
function StartShiftPanel({ onStart }) {
    const { token, showToast } = useContext(AppContext);
    const [admins,        setAdmins]        = useState([]);
    const [photographers, setPhotographers] = useState([]);
    const [locations,     setLocations]     = useState([]);
    const [currentLocationId, setCurrentLocationId] = useState(null);
    const [loading,       setLoading]       = useState(true);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [selectedPhIds, setSelectedPhIds] = useState(new Set());
    const [starting,      setStarting]      = useState(false);
    const [step,          setStep]          = useState(1); // 1 = выбор админа, 2 = выбор фотографов

    const load = useCallback(async () => {
        setLoading(true);
        const [adminsData, photogData, locData] = await Promise.all([
            APIWorkstationGetAdmins(token),
            APIWorkstationPhotographersStatus(token),
            APIGetLocations(token),
        ]);
        if (Array.isArray(adminsData?.admins)) setAdmins(adminsData.admins);
        else if (Array.isArray(adminsData))    setAdmins(adminsData);

        if (Array.isArray(photogData?.photographers)) setPhotographers(photogData.photographers);
        if (photogData?.location_id) setCurrentLocationId(photogData.location_id);
        if (Array.isArray(locData)) setLocations(locData);
        else if (Array.isArray(locData?.locations)) setLocations(locData.locations);
        setLoading(false);
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const togglePh = (id) => {
        setSelectedPhIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleStart = async () => {
        if (!selectedAdmin) { showToast('Выбери себя из списка', 'warning'); return; }
        setStarting(true);
        const res = await onStart(selectedAdmin, Array.from(selectedPhIds));
        setStarting(false);
        if (res?.ok) {
            setSelectedAdmin(null);
            setSelectedPhIds(new Set());
            setStep(1);
        }
    };

    const freePh   = photographers.filter(p => p.status === 'free');
    const busyPh   = photographers.filter(p => p.status === 'busy');

    return (
        <Box sx={{
            bgcolor: 'var(--joy-palette-background-level1)',
            border: '1px solid var(--joy-palette-divider)',
            borderRadius: 'var(--joy-radius-xl)',
            p: { xs: 2, sm: 3 },
        }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 0.5 }}>
                {step === 1 ? 'Начать смену' : 'С кем работаете'}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                {step === 1
                    ? 'Выберите себя из списка администраторов на сегодня'
                    : 'Отметьте фотографов, чьи чеки будут идти на вас. Чтобы снимать самому, отметьте и себя. Позже состав можно изменить в «Фотографы на смене».'}
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <MemeLoader />
                </Box>
            ) : step === 1 ? (
                <>
                    {admins.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <BlockRoundedIcon sx={{ fontSize: 36, color: 'var(--joy-palette-text-tertiary)', mb: 1 }} />
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                                Сегодня нет назначенных администраторов
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={1}>
                            {admins.map(a => {
                                const isSel = selectedAdmin === a.id;
                                return (
                                    <Box
                                        key={a.id}
                                        onClick={() => setSelectedAdmin(a.id)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                            px: 2, py: 1.4,
                                            borderRadius: 'var(--joy-radius-lg)',
                                            border: '1px solid',
                                            borderColor: isSel ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                                            bgcolor: isSel ? 'rgba(162,38,226,0.08)' : 'var(--joy-palette-background-level2)',
                                            cursor: 'pointer', transition: 'all .15s',
                                        }}
                                    >
                                        <Avatar src={a.avatar_url} size="sm" sx={{
                                            width: 36, height: 36, fontSize: 12, fontWeight: 700,
                                            bgcolor: 'rgba(162,38,226,0.15)',
                                            color: 'var(--joy-palette-primary-300)',
                                            border: '1.5px solid var(--joy-palette-primary-500)',
                                        }}>
                                            {(a.first_name || a.full_name || '').charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {a.full_name || a.name}
                                            </Typography>
                                            {a.start_time && (
                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    с {a.start_time}
                                                </Typography>
                                            )}
                                        </Box>
                                        {isSel && <ArrowForwardRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-400)' }} />}
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}

                    <Button
                        fullWidth
                        disabled={!selectedAdmin}
                        onClick={() => setStep(2)}
                        sx={{
                            mt: 2,
                            background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                            color: '#fff', fontWeight: 700, fontSize: 14,
                            boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                        }}
                    >
                        Далее
                    </Button>
                </>
            ) : (
                <>
                    {photographers.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                                Сегодня нет назначенных фотографов на этой точке
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={1}>
                            {freePh.length > 0 && (
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mt: 0.5 }}>
                                    Свободные
                                </Typography>
                            )}
                            {freePh.map(p => {
                                const isSel = selectedPhIds.has(p.photographer_id);
                                return (
                                    <Box
                                        key={p.photographer_id}
                                        onClick={() => togglePh(p.photographer_id)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                            px: 2, py: 1.2,
                                            borderRadius: 'var(--joy-radius-md)',
                                            border: '1px solid',
                                            borderColor: isSel ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                                            bgcolor: isSel ? 'rgba(162,38,226,0.08)' : 'var(--joy-palette-background-level2)',
                                            cursor: 'pointer', transition: 'all .15s',
                                        }}
                                    >
                                        <Box sx={{
                                            width: 18, height: 18,
                                            borderRadius: 4,
                                            border: '2px solid',
                                            borderColor: isSel ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-text-tertiary)',
                                            bgcolor: isSel ? 'var(--joy-palette-primary-500)' : 'transparent',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            {isSel && <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 900, lineHeight: 1 }}>✓</Typography>}
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--joy-palette-text-primary)' }}>
                                                {p.photographer_name}{p.photographer_id === selectedAdmin ? ' — я также фотограф' : ''}
                                            </Typography>
                                            {p.start_time && (
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    с {p.start_time}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                                            <Select
                                                size="sm" placeholder="→ точка" value=""
                                                onChange={async (_, locId) => {
                                                    if (!locId) return;
                                                    if (!window.confirm('Перенести фотографа на другую точку?')) return;
                                                    const res = await APIWorkstationReassignPhotographer(token, p.photographer_id, locId);
                                                    if (res?.error) showToast(parseApiError(res.error, 'Не удалось перенести'), 'danger');
                                                    else { showToast('Фотограф перенесён'); load(); }
                                                }}
                                                sx={{ minWidth: 96 }}
                                            >
                                                {locations
                                                    .filter(l => l.id !== currentLocationId)
                                                    .map(l => <Option key={l.id} value={l.id}>{l.name}</Option>)}
                                            </Select>
                                        </Box>
                                    </Box>
                                );
                            })}

                            {busyPh.length > 0 && (
                                <>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mt: 1 }}>
                                        У других админов
                                    </Typography>
                                    {busyPh.map(p => (
                                        <Box
                                            key={p.photographer_id}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                px: 2, py: 1.2,
                                                borderRadius: 'var(--joy-radius-md)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                bgcolor: 'var(--joy-palette-background-level2)',
                                                opacity: 0.5,
                                            }}
                                        >
                                            <Box sx={{ width: 18, height: 18 }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    {p.photographer_name}
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: '#f59e0b' }}>
                                                    У админа {p.current_admin_name}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </>
                            )}
                        </Stack>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                            variant="soft" color="neutral"
                            onClick={() => setStep(1)}
                            sx={{ fontWeight: 700, fontSize: 13 }}
                        >
                            Назад
                        </Button>
                        <Button
                            sx={{
                                flex: 1,
                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                color: '#fff', fontWeight: 700, fontSize: 14,
                                boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                            }}
                            loading={starting}
                            disabled={starting}
                            onClick={handleStart}
                        >
                            Начать смену {selectedPhIds.size > 0 ? `(${selectedPhIds.size} ф.)` : ''}
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
}

// ════════════════════════════════════════════════════════════════════
//  Подкомпонент: карточка pending-уведомления (быстрое оформление)
// ════════════════════════════════════════════════════════════════════
function PendingSaleCard({ notification, onProcess, onCancel, processing }) {
    const items = notification.items || [];
    const total = parseFloat(notification.total_value || 0);

    return (
        <Box sx={{
            border: '1px solid rgba(245,158,11,0.35)',
            bgcolor: 'rgba(245,158,11,0.06)',
            borderRadius: 'var(--joy-radius-lg)',
            p: 1.8,
            transition: 'all .15s',
        }}>
            {/* Шапка */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
                <Avatar src={notification.photographer_avatar_url} size="sm" sx={{
                    width: 32, height: 32, fontSize: 11, fontWeight: 700,
                    bgcolor: 'rgba(245,158,11,0.15)',
                    color: '#f59e0b',
                    border: '1.5px solid #f59e0b',
                    flexShrink: 0,
                }}>
                    {(notification.photographer_name || '').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                        {notification.photographer_name}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-primary)', fontWeight: 700 }}>
                        {fmtDateTimeRelative(notification.date_create)}
                    </Typography>
                </Box>
                <Typography sx={{
                    fontSize: 18, fontWeight: 800,
                    color: '#f59e0b',
                    fontVariantNumeric: 'tabular-nums',
                }}>
                    {fmtMoney(total)} ₽
                </Typography>
            </Box>

            {/* Состав чека */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: notification.comment ? 1 : 1.5 }}>
                {items.map((it, idx) => {
                    const meta = MONEY_META[it.money_type] || MONEY_META.transfer;
                    return (
                        <Chip key={idx} size="sm" variant="soft"
                            startDecorator={meta.icon}
                            sx={{
                                fontSize: 11, fontWeight: 700,
                                bgcolor: meta.bg, color: meta.color,
                            }}
                        >
                            {fmtMoney(it.value)} ₽ · {meta.label}
                        </Chip>
                    );
                })}
            </Box>

            {/* Комментарий */}
            {notification.comment && (
                <Box sx={{
                    bgcolor: 'var(--joy-palette-background-level2)',
                    borderRadius: 'var(--joy-radius-md)',
                    px: 1.5, py: 1, mb: 1.5,
                    borderLeft: '3px solid #f59e0b',
                }}>
                    <Typography sx={{
                        fontSize: 12, color: 'var(--joy-palette-text-primary)',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                        {notification.comment}
                    </Typography>
                </Box>
            )}

            {/* Кнопки */}
            <Stack direction={"row"} sx={{ gap: 1, justifyContent: 'flex-end' }}>
                <Button
                    size="md"
                    startDecorator={<ReceiptLongRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => onProcess(notification)}
                    disabled={processing}
                    sx={{
                        flex: {
                            'xs': 1,
                            'sm': 0.2
                        },
                        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        color: '#fff',
                        '&:hover': { boxShadow: '0 4px 12px rgba(34,197,94,0.4)' },
                    }}
                >
                    Подтвердить
                </Button>
                <IconButton
                    size="md" variant="solid" color="danger"
                    onClick={() => {
                        // Вызов окна подтверждения
                        const isConfirmed = window?.confirm("Отменить продажу? Чек будет удалён из кассы, выручка пересчитается.");
                        
                        if (isConfirmed) {
                            onCancel(notification);
                        }
                    }}
                    disabled={processing}
                    sx={{
                        // bgcolor: 'var(--joy-palette-background-level2)',
                        // border: '1px solid var(--joy-palette-divider)',
                        // '&:hover': { borderColor: 'var(--joy-palette-danger-400)' },
                    }}
                >
                    <BlockRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Stack>
        </Box>
    );
}

// ════════════════════════════════════════════════════════════════════
//  WorkstationDashboard
// ════════════════════════════════════════════════════════════════════
export default function WorkstationDashboard() {
    const { token, showToast, onRefreshTick, activeShift, activeShifts, startShift, endShift, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const [stats,     setStats]     = useState(null);
    const [pending,   setPending]   = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [endingShift, setEndingShift] = useState(false);
    const [confirmEnd, setConfirmEnd] = useState(false);
    const [processingId, setProcessingId] = useState(null);
    const [addOrderOpen, setAddOrderOpen] = useState(false);

    // Справочники для модалки оформления
    const [products,   setProducts]   = useState([]);
    const [discounts,  setDiscounts]  = useState([]);
    const [categories, setCategories] = useState([]);
    const [processModal, setProcessModal] = useState({ open: false, notification: null, loading: false });

    // ── Загрузка статистики и pending ─────────────────────────────
    const loadAll = useCallback(async () => {
        if (!activeShift) { setLoading(false); return true; }
        setLoading(true);
        const [statsRes, pendingRes, productsRes, discountsRes, categoriesRes] = await Promise.all([
            APIWorkstationStats(token),
            APIGetPendingSaleNotifications(token, { status: 'pending' }),
            APIGetProducts(token, { is_active: true }),
            APIGetDiscounts(token, { is_active: true }),
            APIGetProductCategories(token, { is_active: true, with_counts: true }),
        ]);
        if (statsRes) setStats(statsRes);
        if (Array.isArray(pendingRes)) setPending(pendingRes);
        if (Array.isArray(productsRes)) setProducts(productsRes);
        if (Array.isArray(discountsRes)) setDiscounts(discountsRes);
        if (Array.isArray(categoriesRes)) setCategories(categoriesRes);
        setLoading(false);
        return true;
    }, [token, activeShift]);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => onRefreshTick(loadAll), [onRefreshTick, loadAll]);

    // Тихий авто-поллинг: обновляем только динамику (очередь продаж + статистику),
    // без setLoading и без перезагрузки справочников — страница не мигает.
    useEffect(() => {
        if (!activeShift) return;
        const id = setInterval(async () => {
            if (document.hidden) return;
            const [statsRes, pendingRes] = await Promise.all([
                APIWorkstationStats(token),
                APIGetPendingSaleNotifications(token, { status: 'pending' }),
            ]);
            if (statsRes) setStats(statsRes);
            if (Array.isArray(pendingRes)) setPending(pendingRes);
        }, 10000);
        return () => clearInterval(id);
    }, [activeShift, token]);

    // ── Обработчики ───────────────────────────────────────────────
    const handleEndShift = async () => {
        setEndingShift(true);
        await endShift();
        setEndingShift(false);
        setConfirmEnd(false);
    };

    // Продажа уже в кассе — здесь только подтверждаем (чек НЕ пересоздаётся).
    const handleOpenProcess = async (notification) => {
        setProcessingId(notification.id);
        const res = await APIProcessSaleNotification(token, notification.id, {});
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось подтвердить продажу'), 'danger');
            setProcessingId(null);
            return;
        }
        showToast('Продажа подтверждена');
        setPending(prev => prev.filter(p => p.id !== notification.id));
        setProcessingId(null);
        loadAll();
    };

    // Подтвердить продажу (чек уже в кассе — просто помечаем)
    const handleConfirmOrder = async (o) => {
        if (!o.sale_notification_id) return;
        setProcessingId(o.id);
        const res = await APIProcessSaleNotification(token, o.sale_notification_id, {});
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось подтвердить'), 'danger');
        } else {
            showToast('Продажа подтверждена');
            loadAll();
        }
        setProcessingId(null);
    };

    // Удалить чек (сервер сам отменит связанный фантом)
    const handleDeleteOrder = async (o) => {
        setProcessingId(o.id);
        const res = await APIDeleteOrder(token, o.id);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось удалить чек'), 'danger');
        } else {
            showToast('Чек удалён');
            loadAll();
        }
        setProcessingId(null);
    };

    // Отправить оформленный чек на сервер.
    // payload = { assignments: [...] } из ProcessSaleModal (новый формат).
    const handleSubmitProcess = async (payload) => {
        const notification = processModal.notification;
        if (!notification) return;
        setProcessModal(p => ({ ...p, loading: true }));
        const res = await APIProcessSaleNotification(token, notification.id, payload);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось оформить чек'), 'danger');
            setProcessModal(p => ({ ...p, loading: false }));
            return;
        }
        const total = (res.orders || []).reduce((s, o) => s + parseFloat(o.value || 0), 0);
        showToast(`Чек оформлен · ${total.toLocaleString('ru-RU')} ₽`);
        setPending(prev => prev.filter(p => p.id !== notification.id));
        setProcessModal({ open: false, notification: null, loading: false });
        loadAll();
    };

    const handleCancelNotif = async (notification) => {
        setProcessingId(notification.id);
        const res = await APICancelSaleNotification(token, notification.id);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось отменить'), 'danger');
        } else {
            showToast('Уведомление отменено');
            setPending(prev => prev.filter(p => p.id !== notification.id));
            loadAll();
        }
        setProcessingId(null);
    };

    // ── Статистические значения ───────────────────────────────────
    const today = stats?.today || {};
    const shift = stats?.shift || activeShift || {};

    // ── Если смена не начата → экран выбора админа ────────────────
    if (!activeShift) {
        return (
            <motion.div
                variants={pageVariants} initial="initial" animate="animate" exit="exit"
                style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
            >
                <Header />
                <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                            Рабочий аккаунт
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                            Смена не начата
                        </Typography>
                    </Box>
                    <StartShiftPanel onStart={startShift} />
                </Box>
            </motion.div>
        );
    }

    return (
        <motion.div
            variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                <BirthdayWidget />

                {/* ── Заголовок ─────────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                Рабочий аккаунт
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                Сегодня · {new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                            <Button
                                onClick={() => setAddOrderOpen(true)}
                                startDecorator={<AddRoundedIcon sx={{ fontSize: 18 }} />}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700,
                                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                }}
                            >
                                Добавить чек
                            </Button>
                            <Button
                                startDecorator={<LogoutRoundedIcon sx={{ fontSize: 16 }} />}
                                variant="soft" color="danger"
                                onClick={() => setConfirmEnd(true)}
                            >
                                Завершить смену
                            </Button>
                        </Box>
                    </Box>
                </motion.div>

                {loading && !stats ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={2.5}>

                            {/* ── Активные смены (несколько админов одновременно) ─────── */}
                            {activeShifts.length > 1 && (
                                <motion.div variants={fadeUp}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)',
                                        p: 2,
                                    }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 1.5, textTransform: 'uppercase' }}>
                                            Активные смены ({activeShifts.length})
                                        </Typography>
                                        <Stack spacing={1}>
                                            {activeShifts.map(s => (
                                                <Box key={s.id} sx={{
                                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                                    p: 1.5,
                                                    borderRadius: 'var(--joy-radius-md)',
                                                    bgcolor: 'var(--joy-palette-background-level2)',
                                                    border: '1px solid var(--joy-palette-divider)',
                                                }}>
                                                    <Avatar src={s.administrator_avatar_url} sx={{
                                                        width: 36, height: 36, fontSize: 12, fontWeight: 700,
                                                        bgcolor: 'rgba(162,38,226,0.15)',
                                                        color: 'var(--joy-palette-primary-300)',
                                                        border: '1.5px solid var(--joy-palette-primary-500)',
                                                    }}>
                                                        {(s.administrator_name || '').charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                            {s.administrator_name}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                            Фотографов: {s.photographers?.length || 0} · с {fmtTime(s.started_at)}
                                                        </Typography>
                                                    </Box>
                                                    <Button
                                                        size="sm" variant="outlined" color="danger"
                                                        onClick={() => {
                                                            if (window.confirm(`Завершить смену ${s.administrator_name}?`)) {
                                                                endShift(s.id);
                                                            }
                                                        }}
                                                        sx={{ fontSize: 11, fontWeight: 700 }}
                                                    >
                                                        Завершить
                                                    </Button>
                                                </Box>
                                            ))}
                                        </Stack>

                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => {
                                                // Переключаемся на экран добавления новой смены
                                                // (реализуй через состояние showAddShift или роутинг)
                                                showToast('Чтобы добавить ещё одну смену — завершите все текущие и начните заново, либо реализуй роутинг между экранами', 'warning');
                                            }}
                                            sx={{ mt: 1.5, fontWeight: 700, fontSize: 12 }}
                                        >
                                            + Добавить ещё одного админа
                                        </Button>
                                    </Box>
                                </motion.div>
                            )}

                            {/* ── Hero: смена и админ ────────────────────── */}
                            <motion.div variants={fadeUp}>
                                <Box sx={{
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    border: '1px solid var(--joy-palette-primary-500)',
                                    borderRadius: 'var(--joy-radius-xl)',
                                    p: { xs: 2, sm: 2.5 },
                                    background: 'linear-gradient(135deg, rgba(162,38,226,0.10) 0%, rgba(162,38,226,0.02) 100%)',
                                    position: 'relative', overflow: 'hidden',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                        <Avatar src={shift.administrator_avatar_url} sx={{
                                            width: 56, height: 56, fontSize: 18, fontWeight: 800,
                                            bgcolor: 'rgba(162,38,226,0.20)',
                                            color: 'var(--joy-palette-primary-300)',
                                            border: '2px solid var(--joy-palette-primary-500)',
                                        }}>
                                            {(shift.administrator_name || '').charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                Админ на смене
                                            </Typography>
                                            <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mt: 0.2 }}>
                                                {shift.administrator_name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <LocationOnRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                                        {shift.location_name}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <AccessTimeRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                                        с {fmtTime(shift.started_at)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                        <IconButton
                                            variant="soft"
                                            color="primary"
                                            onClick={loadAll}
                                        >
                                            <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </motion.div>

                            {/* ── Pending уведомления о продажах ─────────── */}
                            <motion.div variants={fadeUp} style={{ position: 'relative', zIndex: 1 }}>
                                <Box sx={{
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    borderRadius: 'var(--joy-radius-xl)',
                                    p: { xs: 2, sm: 2.5 },
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <NotificationsActiveRoundedIcon sx={{
                                                fontSize: 18,
                                                color: pending.length > 0 ? '#f59e0b' : 'var(--joy-palette-text-tertiary)',
                                            }} />
                                            <Typography sx={{
                                                fontSize: 13, fontWeight: 700,
                                                color: 'var(--joy-palette-text-secondary)',
                                                textTransform: 'uppercase', letterSpacing: '0.04em',
                                            }}>
                                                Ожидают оформления
                                            </Typography>
                                            {pending.length > 0 && (
                                                <Chip size="sm" variant="solid" sx={{
                                                    bgcolor: '#f59e0b', color: '#fff', fontWeight: 800, fontSize: 11,
                                                }}>
                                                    {pending.length}
                                                </Chip>
                                            )}
                                            {loading && (
                                                <CircularProgress
                                                    size="sm"
                                                    sx={{ '--CircularProgress-size': '14px', '--CircularProgress-trackThickness': '2px' }}
                                                />
                                            )}
                                        </Box>
                                        <Typography
                                            onClick={() => navigate('/workstation/orders')}
                                            sx={{
                                                fontSize: 12, fontWeight: 700,
                                                color: 'var(--joy-palette-primary-400)',
                                                cursor: 'pointer',
                                                '&:hover': { color: 'var(--joy-palette-primary-300)' },
                                            }}
                                        >
                                            Все чеки →
                                        </Typography>
                                    </Box>

                                    {pending.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', py: 3 }}>
                                            <ReceiptLongRoundedIcon sx={{ fontSize: 36, color: 'var(--joy-palette-text-tertiary)', mb: 1 }} />
                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                                                Нет новых уведомлений о продажах
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Stack spacing={1.2}>
                                            <AnimatePresence>
                                                {pending.map(n => (
                                                    <motion.div
                                                        key={n.id}
                                                        initial={{ opacity: 0, x: -8 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        exit={{ opacity: 0, x: 8, transition: { duration: 0.18 } }}
                                                    >
                                                        <PendingSaleCard
                                                            notification={n}
                                                            onProcess={(n) => setProcessModal({ open: true, notification: n, loading: false })}
                                                            onCancel={handleCancelNotif}
                                                            processing={processingId === n.id}
                                                        />
                                                    </motion.div>
                                                ))}
                                            </AnimatePresence>
                                        </Stack>
                                    )}
                                </Box>
                            </motion.div>

                            {/* ── Фотографы на смене ─────────────────────── */}
                            {shift?.id && (
                                <motion.div variants={fadeUp} style={{ position: 'relative', zIndex: 2 }}>
                                    <PointPhotographersPanel
                                        shiftId={shift.id}
                                        myAdminId={shift.administrator_id}
                                    />
                                </motion.div>
                            )}

                            {/* ── Быстрые действия ─────────────────────── */}
                            <motion.div variants={fadeUp} style={{ position: 'relative', zIndex: 2 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: 1.5, position: 'relative', zIndex: 2 }}>
                                    {[
                                        { label: 'Чеки и продажи', icon: <ReceiptLongRoundedIcon sx={{ fontSize: 22 }} />, color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.08)', to: '/workstation/orders' },
                                        { label: 'Штрафы',         icon: <GavelRoundedIcon       sx={{ fontSize: 22 }} />, color: '#ef4444',                            bg: 'rgba(239,68,68,0.08)',  to: '/workstation/fines'  },
                                        { label: 'Статистика',     icon: <BarChartRoundedIcon    sx={{ fontSize: 22 }} />, color: '#06b6d4',                            bg: 'rgba(6,182,212,0.08)',  to: '/workstation/stats'  },
                                    ].map(b => (
                                        <Box
                                            key={b.label}
                                            onClick={() => navigate(b.to)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                px: 2.2, py: 2,
                                                position: 'relative', zIndex: 2,
                                                pointerEvents: 'auto',
                                                borderRadius: 'var(--joy-radius-lg)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                bgcolor: b.bg,
                                                cursor: 'pointer', transition: 'all .15s',
                                                '&:hover': { borderColor: b.color, transform: 'translateY(-1px)' },
                                            }}
                                        >
                                            <Box sx={{ color: b.color }}>{b.icon}</Box>
                                            <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {b.label}
                                            </Typography>
                                            <ArrowForwardRoundedIcon sx={{ fontSize: 16, color: b.color }} />
                                        </Box>
                                    ))}
                                </Box>
                            </motion.div>

                        </Stack>
                    </motion.div>
                )}
            </Box>

            <WorkstationAddOrderModal
                open={addOrderOpen}
                onClose={() => setAddOrderOpen(false)}
                onCreated={loadAll}
                locationId={activeShift?.location?.id || activeShift?.location}
            />

            <ProcessSaleModal
                open={processModal.open}
                notification={processModal.notification}
                products={products}
                discounts={discounts}
                categories={categories}
                loading={processModal.loading}
                onClose={() => setProcessModal({ open: false, notification: null, loading: false })}
                onSubmit={handleSubmitProcess}
                canSetDate={hasPerm('finance.orders.set_date')}
            />

            {/* ── Модалка подтверждения завершения смены ──────────── */}
            <Modal open={confirmEnd} onClose={() => setConfirmEnd(false)}>
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid var(--joy-palette-divider)',
                    maxWidth: 380,
                    maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
                }}>
                    <ModalClose />
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 1 }}>
                        Завершить смену?
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                        После завершения ты не сможешь создавать чеки и штрафы от имени админа{shift.administrator_name ? ` «${shift.administrator_name}»` : ''}. За то с кайфом отдохнуть!
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="soft" color="neutral" size="sm" onClick={() => setConfirmEnd(false)}>
                            Отмена
                        </Button>
                        <Button color="danger" size="sm" loading={endingShift} onClick={handleEndShift}>
                            Завершить
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </motion.div>
    );
}